/**
 * IndianAccents.jsx
 * Indian cultural visual elements for Zentrust
 * Features saffron, peacock green, and Ashoka blue gradients
 */

export function SaffronDivider() {
  return (
    <div className="w-full h-1 bg-gradient-to-r from-[hsl(var(--zt-gold))] via-[hsl(var(--zt-sage))] to-transparent opacity-60" />
  );
}

export function AshokaPattern() {
  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none"
      viewBox="0 0 1440 1024"
      preserveAspectRatio="none"
    >
      <defs>
        <pattern id="ashoka-dots" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
          <circle cx="30" cy="30" r="3" fill="currentColor" />
        </pattern>
      </defs>
      <rect width="1440" height="1024" fill="url(#ashoka-dots)" />
    </svg>
  );
}

export function PeacockAccent() {
  return (
    <div className="relative">
      <div
        className="absolute inset-0 rounded-full opacity-20 blur-2xl"
        style={{
          background: "radial-gradient(circle, hsl(var(--zt-sage)) 0%, transparent 70%)",
        }}
      />
    </div>
  );
}

export function FCNRCard({ title, value, subtitle, icon: Icon }) {
  return (
    <div className="rounded-xl border-2 border-[hsl(var(--zt-gold))]/30 bg-gradient-to-br from-[hsl(var(--zt-beige))] to-white p-6 hover:border-[hsl(var(--zt-gold))]/60 transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-heading text-sm font-semibold text-[hsl(var(--zt-teal-deep))]">{title}</h4>
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--zt-gold))]/15 grid place-items-center">
            <Icon size={18} className="text-[hsl(var(--zt-gold))]" />
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-[hsl(var(--zt-gold))] font-numeric">{value}</p>
      {subtitle && <p className="text-xs text-[hsl(var(--zt-muted))] mt-2">{subtitle}</p>}
    </div>
  );
}

/**
 * IndianFlag inspired header divider
 * Saffron-White-Green tricolor
 */
export function IndianTricolorBar() {
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full">
      <div className="flex-1 bg-[hsl(var(--zt-gold))]" />
      <div className="flex-1 bg-white" />
      <div className="flex-1 bg-[hsl(var(--zt-sage))]" />
    </div>
  );
}

/**
 * Cultural heritage badge for NRI-specific features
 */
export function NRIBadge({ text, variant = "saffron" }) {
  const variants = {
    saffron: "from-[hsl(var(--zt-gold))]/20 to-[hsl(var(--zt-gold))]/5 text-[hsl(var(--zt-gold))]",
    peacock: "from-[hsl(var(--zt-sage))]/20 to-[hsl(var(--zt-sage))]/5 text-[hsl(var(--zt-sage))]",
    ashoka: "from-[hsl(var(--zt-teal))]/20 to-[hsl(var(--zt-teal))]/5 text-[hsl(var(--zt-teal))]",
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-heading font-bold bg-gradient-to-r ${variants[variant]} border border-current/20`}
    >
      {text}
    </span>
  );
}

/**
 * India map hint with accent
 */
export function IndiaMapAccent() {
  return (
    <div className="relative w-32 h-40 opacity-10 pointer-events-none">
      <svg viewBox="0 0 100 120" className="w-full h-full fill-[hsl(var(--zt-gold))]">
        <path d="M50,10 L60,25 L65,35 L60,45 L50,50 L40,45 L35,35 L40,25 Z M50,50 L70,60 L75,75 L70,85 L50,90 L30,85 L25,75 L30,60 Z" />
      </svg>
    </div>
  );
}
